import React, { useState, useMemo } from 'react';
import { Save, Plus, Loader2, Calendar, Banknote, History, Edit3, X } from 'lucide-react';
import { BranchIncomeRecord } from '../../types/db';

interface IncomeManagerProps {
  onSave: (data: { description: string; amount: number; date: string }) => Promise<void>;
  onUpdate: (
    id: string,
    data: { description: string; amount: number; date: string }
  ) => Promise<void>;
  incomes: BranchIncomeRecord[];
  branchId: string;
}

export function IncomeManager({ onSave, onUpdate, incomes, branchId }: IncomeManagerProps) {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Filter log berdasarkan cabang aktif
  const filteredLogs = useMemo(() => {
    return incomes.filter((inc) => branchId === 'ALL' || inc.branch_id === branchId).slice(0, 50); // Tampilkan 50 data terakhir
  }, [incomes, branchId]);

  const handleSave = async () => {
    if (!description || amount <= 0 || !date) {
      alert('Mohon lengkapi deskripsi, nominal, dan tanggal pemasukan.');
      return;
    }
    setLoading(true);
    try {
      if (editingId) {
        await onUpdate(editingId, { description, amount, date });
        window.alert('Data pemasukan berhasil diperbarui!');
      } else {
        await onSave({ description, amount, date });
        window.alert('Data pemasukan berhasil disimpan!');
      }
      handleCancelEdit();
    } catch (e: any) {
      alert('Gagal memproses data: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (log: BranchIncomeRecord) => {
    setEditingId(log.id);
    setDescription(log.description);
    setAmount(log.amount);
    setDate(log.date);
    // Scroll to form (top)
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setDescription('');
    setAmount(0);
    setDate(new Date().toISOString().split('T')[0]);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fade-in font-sans">
      {/* PANEL KIRI: FORM INPUT */}
      <div
        className={`p-10 rounded-[3rem] border transition-all shadow-sm space-y-8 h-fit ${editingId ? 'bg-indigo-50/50 border-indigo-200' : 'bg-white border-slate-100'}`}
      >
        <div className="flex items-center gap-4">
          <div
            className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm ${editingId ? 'bg-indigo-600 text-white' : 'bg-emerald-100 text-emerald-600'}`}
          >
            {editingId ? <Edit3 className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
          </div>
          <div>
            <h3 className="font-black text-2xl text-slate-900 uppercase tracking-tight leading-none">
              {editingId ? 'Koreksi Data' : 'Input Income'}
            </h3>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
              {editingId ? 'Memperbaiki kesalahan input data' : 'Pencatatan Pemasukan Kas Unit'}
            </p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
              Tanggal Transaksi
            </label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-300 pointer-events-none" />
              <input
                type="date"
                className="w-full border-2 border-slate-50 pl-12 pr-4 py-4 rounded-2xl outline-none focus:border-indigo-300 font-bold bg-slate-50/50 text-slate-800 transition-all"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">
              Deskripsi Transaksi
            </label>
            <input
              className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none focus:border-indigo-300 font-bold bg-slate-50/50 text-slate-800 transition-all"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Misal: Pembayaran SPP Januari Siswa X"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">
              Nominal (Rp)
            </label>
            <div className="relative">
              <Banknote className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-300 pointer-events-none" />
              <input
                className="w-full border-2 border-slate-50 pl-12 pr-4 py-4 rounded-2xl outline-none focus:border-emerald-300 font-black text-2xl bg-slate-50/50 text-indigo-900"
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                placeholder="0"
              />
            </div>
          </div>

          <div className="flex gap-3">
            {editingId && (
              <button
                onClick={handleCancelEdit}
                className="flex-1 bg-slate-200 text-slate-600 py-6 rounded-[2rem] font-black uppercase tracking-widest hover:bg-slate-300 transition-all flex justify-center items-center gap-2"
              >
                <X className="w-5 h-5" /> BATAL
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={loading}
              className={`flex-[2] py-6 rounded-[2rem] font-black uppercase tracking-[0.2em] shadow-2xl transition-all disabled:opacity-50 flex justify-center items-center gap-3 active:scale-95 ${editingId ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-slate-900 hover:bg-black text-white'}`}
            >
              {loading ? (
                <Loader2 className="animate-spin w-6 h-6" />
              ) : editingId ? (
                <Save className="w-6 h-6" />
              ) : (
                <Plus className="w-6 h-6" />
              )}
              {loading ? 'MEMPROSES...' : editingId ? 'PERBARUI DATA' : 'SIMPAN PEMASUKAN'}
            </button>
          </div>
        </div>
      </div>

      {/* PANEL KANAN: ACTIVITY LOG */}
      <div className="bg-slate-50 p-8 rounded-[3.5rem] border border-slate-100 flex flex-col h-[600px]">
        <div className="flex items-center justify-between mb-6 px-4">
          <div className="flex items-center gap-3">
            <History className="text-slate-400 w-5 h-5" />
            <h4 className="font-black text-slate-800 uppercase text-xs tracking-widest">
              Log Income Terkini
            </h4>
          </div>
          <span className="text-[8px] font-black text-indigo-600 bg-white px-3 py-1 rounded-full border border-slate-200 uppercase tracking-widest">
            Aksi Koreksi Aktif
          </span>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 no-scrollbar space-y-3">
          {filteredLogs.map((log, idx) => (
            <div
              key={idx}
              className={`bg-white p-5 rounded-3xl border flex justify-between items-center group transition-all shadow-sm ${editingId === log.id ? 'border-indigo-500 ring-2 ring-indigo-100' : 'border-slate-100 hover:border-indigo-200'}`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs ${editingId === log.id ? 'bg-indigo-600 text-white' : 'bg-emerald-50 text-emerald-500'}`}
                >
                  {idx + 1}
                </div>
                <div>
                  <p className="font-black text-slate-800 uppercase text-[11px] truncate max-w-[150px]">
                    {log.description}
                  </p>
                  <p className="text-[9px] text-slate-400 font-bold uppercase mt-0.5">
                    {log.date} • {log.branch_id}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="font-black text-indigo-600 text-sm">
                    Rp {Number(log.amount).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEditClick(log)}
                    className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-600 hover:text-white transition-all"
                    title="Edit / Koreksi"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredLogs.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-slate-300 opacity-50">
              <History className="w-12 h-12 mb-4" />
              <p className="font-black uppercase tracking-widest text-[10px]">
                Belum ada riwayat transaksi
              </p>
            </div>
          )}
        </div>

        <div className="pt-6 mt-6 border-t border-slate-200 text-center">
          <p className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.3em]">
            Integrity Financial Control Engine
          </p>
        </div>
      </div>
    </div>
  );
}
